import { useRef, useCallback } from 'react';

interface PendingRequest {
  promise: Promise<any>;
  abortController: AbortController;
  requestKey: string;
}

/**
 * Hook for deduplicating requests and managing in-flight operations
 * Prevents multiple identical requests from being sent simultaneously
 */
export function useRequestDeduplication() {
  const pendingRequests = useRef<Map<string, PendingRequest>>(new Map());

  const dedupedRequest = useCallback(async <T>(
    requestKey: string,
    requestFn: (signal: AbortSignal) => Promise<T>,
    options?: {
      // If true, will cancel the existing request and start a new one
      replaceExisting?: boolean;
      // Custom key generator for complex request identification
      keyGenerator?: (...args: any[]) => string;
    }
  ): Promise<T> => {
    const { replaceExisting = true } = options || {};

    // Check if we already have this request in flight
    const existing = pendingRequests.current.get(requestKey);
    
    if (existing && !replaceExisting) {
      // Return the existing promise - this deduplicates the request
      return existing.promise;
    }

    if (existing && replaceExisting) {
      // Cancel the existing request
      existing.abortController.abort();
      pendingRequests.current.delete(requestKey);
    }

    // Create new request with AbortController
    const abortController = new AbortController();
    
    const promise = requestFn(abortController.signal)
      .finally(() => {
        // Clean up after request completes
        pendingRequests.current.delete(requestKey);
      });

    // Store the pending request
    pendingRequests.current.set(requestKey, {
      promise,
      abortController,
      requestKey
    });

    return promise;
  }, []);

  // Cancel a specific request
  const cancelRequest = useCallback((requestKey: string) => {
    const pending = pendingRequests.current.get(requestKey);
    if (pending) {
      pending.abortController.abort();
      pendingRequests.current.delete(requestKey);
    }
  }, []);

  // Cancel all pending requests (useful for cleanup)
  const cancelAllRequests = useCallback(() => {
    pendingRequests.current.forEach(request => {
      request.abortController.abort();
    });
    pendingRequests.current.clear();
  }, []);

  // Get the count of pending requests
  const getPendingCount = useCallback(() => {
    return pendingRequests.current.size;
  }, []);

  return {
    dedupedRequest,
    cancelRequest,
    cancelAllRequests,
    getPendingCount
  };
}